import { BRAND_NAME } from './config/brand';
import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { RideWorkspace } from "./components/RideWorkspace";
import { useRideImport } from "./hooks/use-ride-import";
import { TrackPicker } from "./components/TrackPicker";
import type { Ride } from "./domain/ride";
import { installRenderBridge } from "./render/render-bridge";
import { rememberRideName, restoreRideName } from './services/ride-names';
import { normalizeRideName } from './domain/ride-name';
import { readCompositionOptions } from './render/composition-options';
import type { VideoPresentation } from '../../renderer/src/video-presentation';
import { DEFAULT_DEPTH_OF_FIELD, type DepthOfFieldSettings, type DepthOfFieldPreview } from '../../renderer/src/depth-of-field';
import type { CinematicSequence } from '../../renderer/src/cinematic-sequence';
import type { LiveComposition } from './render/live-composition';
import { useVideoConfiguration } from './hooks/use-video-configuration';
import type { StudioDraft } from './studio/studio-draft';
import { readCompositionFile } from './studio/composing/composition-file';
import { TraceImport } from './studio/TraceImport';
import { useStudioProject } from './hooks/use-studio-project';
import { useLocalStudioSample } from './studio/use-local-studio-sample';

/** Open the selected recording or saved composition through production storage. */
export function Studio() {
  const [opening, setOpening] = useState(0);
  const [ride, setRide] = useState<Ride | null>(null);
  const [initialDraftSource, setInitialDraftSource] = useState<'file'>();
  const [initialDraft, setInitialDraft] = useState<StudioDraft>();
  const [progress, setProgress] = useState(0);
  const [presentation, setPresentation] = useState<VideoPresentation | undefined>(() => readCompositionOptions(window.location.search).presentation);
  const [presentationRevision, setPresentationRevision] = useState(0);
  const [cinematicSequence, setCinematicSequence] = useState<CinematicSequence | undefined>();
  const [composition, setComposition] = useState<LiveComposition>();
  const [depthOfField, setDepthOfField] = useState<DepthOfFieldSettings>(() => readCompositionOptions(window.location.search).depthOfField ?? { ...DEFAULT_DEPTH_OF_FIELD });
  const [focusPreview, setFocusPreview] = useState<DepthOfFieldPreview>({ original: false, mask: false });
  const importer = useRideImport(next => { setInitialDraft(undefined); setInitialDraftSource(undefined); setRide(restoreRideName(next)); setProgress(0); });
  const [compositionError, setCompositionError] = useState('');
  const importState = compositionError ? { kind: 'error' as const, message: compositionError } : importer.state;
  const openFile = async (file: File) => {
    setCompositionError('');
    if (!file.name.toLowerCase().endsWith('.json')) { await importer.importFile(file); return; }
    try { const saved = readCompositionFile(JSON.parse(await file.text())); setInitialDraft(saved.draft); setInitialDraftSource('file'); setRide(saved.ride); setProgress(0); setOpening(value => value + 1); }
    catch (cause) { setCompositionError(cause instanceof Error ? cause.message : 'The composition could not be opened.'); }
  };
  const picker = <TrackPicker tracks={importer.tracks} format={importer.choiceFormat} onSelect={importer.accept} onCancel={importer.cancel} />;
  const renderMode = new URLSearchParams(window.location.search).get("render") === "video";
  const videoId = renderMode ? null : new URLSearchParams(window.location.search).get('video');
  const savedVideo = useVideoConfiguration(videoId);
  const projectId = renderMode ? null : new URLSearchParams(window.location.search).get('project');
  const savedProject = useStudioProject(projectId);
  const loadRenderedRide = useCallback((next: Ride) => { setRide(next); }, []);
  const openLocalSample = useCallback((next: Ride, draft: StudioDraft) => {
    setInitialDraft(draft); setRide(next); setProgress(0);
  }, []);
  useLocalStudioSample(renderMode || !!videoId || !!projectId, openLocalSample);

  useEffect(() => {
    if (!savedVideo.configuration) return;
    setInitialDraft(savedVideo.configuration.draft);
    setRide(savedVideo.configuration.ride); setProgress(0);
  }, [savedVideo.configuration]);
  useEffect(() => {
    if (!savedProject.configuration) return;
    setInitialDraft(savedProject.configuration.draft); setRide(savedProject.configuration.ride); setProgress(savedProject.configuration.draft.progress);
  }, [savedProject.configuration]);

  useEffect(() => {
    if (!renderMode) return;
    const bridge = installRenderBridge(loadRenderedRide, progress => {
      flushSync(() => setProgress(progress));
    }, (next, revision) => flushSync(() => { setPresentation(next); setPresentationRevision(revision); }),
    (next, revision, preview) => flushSync(() => { setDepthOfField(next); setFocusPreview(preview); setPresentationRevision(revision); }),
    (next, revision) => flushSync(() => { setCinematicSequence(next); setPresentationRevision(revision); }),
    (next, revision) => flushSync(() => { setComposition(next); setPresentationRevision(revision); }));
    document.documentElement.classList.add("render-mode");
    return () => {
      bridge.dispose();
      delete window.__rideTraceRender;
      document.documentElement.classList.remove("render-mode");
    };
  }, [loadRenderedRide, renderMode]);

  if (ride !== null) return <>{picker}<RideWorkspace key={`${ride.id}:${opening}`} ride={ride} progress={progress}
    initialDraft={initialDraft} initialDraftSource={initialDraftSource}
    renderPresentation={presentation} presentationRevision={presentationRevision}
    renderDepthOfField={depthOfField} focusPreview={focusPreview}
    renderCinematicSequence={cinematicSequence}
    renderComposition={composition}
    setProgress={setProgress} importState={importState} onRideSelected={(file) => void openFile(file)}
    onRenameRide={(value) => {
      const title = normalizeRideName(value);
      const remembered = rememberRideName(ride, title);
      setRide({ ...ride, title });
      return remembered;
    }}
    onClearRide={() => { setRide(null); setInitialDraft(undefined); importer.reset(); }} /></>;

  if (renderMode) return <main className="map-loading">Loading selected trace</main>;
  if (projectId) return <main className="composing-studio entry-page" data-theme="light"><p role={savedProject.error ? 'alert' : 'status'}>{savedProject.error || 'Loading saved Studio project…'}</p></main>;
  if (videoId) return <main className="composing-studio entry-page" data-theme="light">
    <header className="masthead"><a className="wordmark" href="/">{BRAND_NAME}</a><nav><a href="/videos">My videos</a></nav></header>
    {savedVideo.error ? <section><h1>Unable to reopen video</h1><p role="alert">{savedVideo.error}</p>
      <button type="button" onClick={savedVideo.retry}>Try again</button><p><a href="/studio">Open a trace file</a></p></section>
      : <p role="status">Loading your trace and video settings…</p>}
  </main>;
  return <TraceImport accept=".fit,.gpx,.tcx,.json" busy={importState.kind === 'loading'} message={importState.message} error={importState.kind === 'error'} onFile={file => void openFile(file)}>{picker}</TraceImport>;
}
